import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Check, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price_monthly: 29,
    price_annual: 290,
    features: ['Up to 5 projects', 'Basic reporting', 'Email support']
  },
  {
    id: 'pro',
    name: 'Pro',
    price_monthly: 69,
    price_annual: 690,
    features: ['Up to 25 projects', 'Advanced reporting', 'Priority support', 'Team collaboration'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price_monthly: 199,
    price_annual: 1990,
    features: ['Unlimited projects', 'Custom integrations', 'Dedicated support', 'API access']
  }
];

const cardStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      '::placeholder': { color: '#94a3b8' },
      fontFamily: 'inherit'
    },
    invalid: { color: '#ef4444' }
  }
};

function SubscribeForm({ user, selectedTier, billingCycle, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: user.name,
          email: user.email
        }
      });

      if (pmError) {
        setError(pmError.message);
        setIsProcessing(false);
        return;
      }

      // Call backend to create subscription for existing user
      const response = await apiService.createSubscriptionForUser(user.id, {
        tier_id: selectedTier.id,
        billing_cycle: billingCycle,
        payment_method_id: paymentMethod.id
      });

      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const price = billingCycle === 'annual' ? selectedTier.price_annual : selectedTier.price_monthly;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-slate-50 rounded-lg p-4 border">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium">{selectedTier.name} Plan</span>
          <span className="font-bold text-lg">${price}<span className="text-sm font-normal text-slate-500">/{billingCycle === 'annual' ? 'year' : 'month'}</span></span>
        </div>
        <p className="text-sm text-green-600">30-day free trial included</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Card Details</label>
        <div className="p-4 border rounded-lg bg-white">
          <CardElement options={cardStyle} />
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>30-day free trial</strong> - Your card will not be charged until the trial ends. Cancel anytime.
        </p>
      </div>

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
      >
        {isProcessing ? 'Processing...' : 'Start Free Trial'}
      </Button>
    </form>
  );
}

function Subscribe({ user }) {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(TIERS.find(t => t.id === 'pro'));
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [stripePromise, setStripePromise] = useState(null);
  const [step, setStep] = useState(1); // 1: Select Plan, 2: Payment

  useEffect(() => {
    const loadStripeKey = async () => {
      try {
        const res = await apiService.getStripePublishableKey();
        if (res.data?.publishable_key) {
          setStripePromise(loadStripe(res.data.publishable_key));
        }
      } catch (error) {
        console.error('Failed to load Stripe:', error);
        toast.error('Failed to load payment system');
      }
    };
    loadStripeKey();
  }, []);

  const handleSuccess = (data) => {
    toast.success('Subscription started! Enjoy your 30-day free trial.');
    navigate('/profile');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => step === 1 ? navigate('/profile') : setStep(1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? 'Back to Profile' : 'Back to Plans'}
        </Button>

        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
                <Crown className="w-7 h-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl">
              {step === 1 ? 'Choose Your Plan' : 'Complete Your Subscription'}
            </CardTitle>
            <CardDescription>
              {step === 1 ? 'Start with a 30-day free trial' : 'Enter your payment details'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {step === 1 ? (
              <div className="space-y-6">
                {/* Billing Toggle */}
                <div className="flex justify-center">
                  <div className="bg-slate-100 rounded-full p-1 flex">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        billingCycle === 'monthly' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingCycle('annual')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        billingCycle === 'annual' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
                      }`}
                    >
                      Annual <span className="text-green-600 text-xs ml-1">Save 17%</span>
                    </button>
                  </div>
                </div>

                {/* Tier Cards */}
                <div className="space-y-3">
                  {TIERS.map((tier) => (
                    <div
                      key={tier.id}
                      onClick={() => setSelectedTier(tier)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTier?.id === tier.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${tier.popular ? 'ring-2 ring-blue-200' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{tier.name}</h3>
                          {tier.popular && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">Popular</span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">
                            ${billingCycle === 'annual' ? tier.price_annual : tier.price_monthly}
                          </p>
                          <p className="text-xs text-slate-500">/{billingCycle === 'annual' ? 'year' : 'month'}</p>
                        </div>
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1">
                        {tier.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full h-12 text-lg"
                  disabled={!selectedTier}
                >
                  Continue to Payment
                </Button>
              </div>
            ) : (
              <>
                {stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <SubscribeForm
                      user={user}
                      selectedTier={selectedTier}
                      billingCycle={billingCycle}
                      onSuccess={handleSuccess}
                    />
                  </Elements>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-slate-500">Loading payment form...</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Subscribe;
